/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.parallelcoordinates

import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.high_d.axis.group.AxisGroupModel
import com.macrofocus.high_d.axis.group.AxisGroupView
import org.mkui.canvas.CPCanvas
import org.mkui.canvas.MouseEvent
import org.mkui.canvas.MouseListener
import org.mkui.color.MkColor
import org.mkui.color.colorOf
import org.mkui.geom.GeneralPath
import org.mkui.geom.Point2D
import org.mkui.geom.Rectangle
import org.mkui.geom.Rectangle2D
import org.mkui.graphics.AbstractIDrawing
import org.mkui.graphics.IGraphics

/**
 * The header of a group of axes, as SwingAxisGroupView: the name of the group, collapsing or expanding it when clicked
 * (as the collapsible header of SwingAxisGroupView, whatever [AxisGroupModel.isCollapsible] says).
 */
class AxisGroupHeaderView(private val group: AxisGroupModel<*, *>) : CPCanvas(), AxisGroupView {
    private val drawing = object : AbstractIDrawing() {
        override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
            if (width <= 0 || height <= 0) {
                return
            }
            g.setColor(FACE)
            g.fillRectangle(0, 0, width.toInt(), height.toInt())
            g.setColor(BORDER)
            g.setLineWidth(1.0)
            g.drawShape(Rectangle2D.Double(0.5, 0.5, width - 1, height - 1))

            g.setFont(AxisHeaderView.FONT)
            g.setColor(TEXT)
            g.setTextBaseline(IGraphics.TextBaseline.Middle)
            var x = PADDING
            run {
                // A triangle pointing right when collapsed, down when expanded (the fonts may not have the glyphs)
                val cy = height / 2
                val h = ARROW / 2
                val arrow = GeneralPath()
                if (group.isCollapsed) {
                    arrow.moveTo(x + 1, cy - h)
                    arrow.lineTo(x + 1 + h * 1.5, cy)
                    arrow.lineTo(x + 1, cy + h)
                } else {
                    arrow.moveTo(x, cy - h * 0.75)
                    arrow.lineTo(x + ARROW, cy - h * 0.75)
                    arrow.lineTo(x + h, cy + h * 0.75)
                }
                arrow.closePath()
                g.fillShape(arrow)
                x += ARROW + PADDING
            }
            g.drawString(AxisHeaderView.elide(g, group.name ?: "", width - x - PADDING), x.toFloat(), (height / 2).toFloat())
        }

        fun changed() {
            notifyIDrawingChanged()
        }
    }

    private val collapsedListener = object : PropertyListener<Boolean> {
        override fun propertyChanged(event: PropertyEvent<Boolean>) {
            drawing.changed()
        }
    }

    init {
        addLayer(drawing)
        group.collapsedProperty().addPropertyListener(collapsedListener)
        addMouseListener(object : MouseListener {
            override fun mouseClicked(e: MouseEvent) {
            }

            override fun mousePressed(e: MouseEvent) {
                e.stopPropagation()
                if (!e.isPopupTrigger) {
                    group.isCollapsed = !group.isCollapsed
                }
            }

            override fun mouseReleased(e: MouseEvent) {
                e.stopPropagation()
            }

            override fun mouseEntered(e: MouseEvent) {
            }

            override fun mouseExited(e: MouseEvent) {
            }
        })
    }

    fun dispose() {
        group.collapsedProperty().removePropertyListener(collapsedListener)
    }

    private companion object {
        const val PADDING = 4.0
        const val ARROW = 8.0

        val FACE: MkColor = colorOf(232, 232, 232)
        val BORDER: MkColor = colorOf(190, 190, 190)
        val TEXT: MkColor = colorOf(0, 0, 0)
    }
}
